import { useState } from "react";
import { useTheme } from "../context/ThemeContext";

export default function Projects() {
  const [activeFilter, setActiveFilter] = useState("All");
  const { isDarkMode } = useTheme();
  
  const categories = ["All", "Work", "School", "Solo"];
  
  const projects = [
    {
      title: "George AI",
      description:
        "A brand AI assistant designed for the brand clients of Boom Technologies.", 
      techStack: ["Unity", "Vue", "InertiaJS", "TailwindCSS", "TypeScript", "C#", "OpenAI API", "Laravel", "PHP", "MySQL", "Redis", "SSH", "Postman"],
      imgSrc: "assets/boomtech.png",
      category: "Work"
    },
    {
      title: "Personal Blog Website",
      description:
        "A personal MERN blog where I share insights from my journey as a student, book lover, traveler, and tech enthusiast, featuring an AI assistant and comment functionality.",
      techStack: ["MongoDB", "ExpressJS", "React", "NodeJS", "TailwindCSS", "Vercel"],
      liveLink: "https://personal-mern-blog.vercel.app/",
      repoCode: "https://github.com/jessilebonzo/Personal-Mern-Blog",
      category: "Solo"
    },
    {
      title: "Bukid Bahay 2D Game",
      description:
        "A 2D desktop farming game that highlights Philippine crops and farms while promoting sustainable farming practices. I also developed a website to showcase our proposed 2D game capstone project.",
      techStack: ["Unity", "C#", "Procreate", "Aseprite"],
      liveLink: "https://bukidbahaycropsbattle.netlify.app/",
      imgSrc: "assets/bukidbahay.png",
      repoCode: "https://drive.google.com/drive/folders/1Rq4qIyxcxsLrqiofeRtCgjPrRJkljBYb?usp=sharing",
      category: "School"
    },
    {     
      title: "Flamehub Website & Mobile",
      description:
        "A platform where users can create posts and share their ideas, experiences, and insights with the entire school community.",
      techStack: ["NextJS", "Prisma", "TailwindCSS", "Vercel", "ExpressJS", "NodeJS", "PlanetScale", "Insomnia", "Flutter", "Dart"],
      imgSrc: "assets/Flamehub.png",
      category: "School"
    },
    {
      title: "WebCraft Website",
      description:
        "A user-friendly website-building platform that empowers individuals and small businesses to create professional websites.",
      techStack: ["HTML", "CSS", "JavaScript", "Bootstrap", "Firebase", "Vercel"],
      liveLink: "https://webcraft-easywebsitecreation.vercel.app/",
      repoCode: "https://github.com/jessilebonzo/WebCraft",
      category: "School"
    },
    {
      title: "Bookshelf",
      description:
        "A personal Library App I developed to catalog every book I've read, along with my favorite quotes. It also allows me to share my collection and explore inspiring reads.",
      techStack: ["HTML", "CSS", "JavaScript", "Netlify"],
      liveLink: "https://booksthatpiquemyinterest.netlify.app/",
      repoCode: "https://github.com/jessilebonzo/Library-App",
      category: "Solo"
    },
  ];

  const filteredProjects = activeFilter === "All" 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  const getThemeBasedClasses = (darkClasses, lightClasses) => 
    isDarkMode ? darkClasses : lightClasses;
  
  const needsImagePreview = (title) => 
    ["George AI", "Bukid Bahay 2D Game", "Flamehub Website & Mobile"].includes(title);
  
  const getIframeStyles = (title) => {
    const scale = title === "Bookshelf" ? 0.25 : 0.3;
    const translateY = title === "Bookshelf" 
      ? "-120px" 
      : title === "Personal Blog Website" ? "-40px" : "0";
    
    return {
      transform: `scale(${scale}) translateY(${translateY})`,
      transformOrigin: "0 0",
      width: `calc(100% / ${scale})`,
      height: title === "Bookshelf" 
        ? `calc(160px / ${scale})` 
        : title === "Personal Blog Website" 
          ? `calc(160px / ${scale} + 40px)` 
          : `calc(160px / ${scale})`,
      border: "none",
      backgroundColor: title === "Personal Blog Website" ? "white" : "transparent"
    };
  };

  return (
    <div 
      id="projects"
      className={`${getThemeBasedClasses('bg-gray-900 text-white', 'bg-gray-300 text-gray-800')} py-10 px-5 transition-colors duration-300`}
    >
      <h2 className="text-3xl font-bold mb-3" style={{ color: "#83329A" }}>Projects</h2>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-center mb-8">
          <div className={`${getThemeBasedClasses('bg-gray-800', 'bg-gray-200')} p-2 rounded-full inline-flex gap-2 shadow-lg transition-colors duration-300`}>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveFilter(category)}
                className={`px-4 py-2 rounded-full w-13 h-13 flex items-center justify-center ${
                  category === activeFilter
                    ? "bg-purple-600 text-white"
                    : getThemeBasedClasses(
                        "bg-gray-800 text-gray-400 hover:bg-gray-700",
                        "bg-gray-300 text-gray-600 hover:bg-gray-400"
                      )
                } transition-colors duration-300`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project, index) => (
            <div
              key={index}
              className={`${getThemeBasedClasses(
                'bg-[#13131a] border-[#27272f] hover:bg-[#83329A]',
                'bg-white border-gray-200 hover:bg-[#A988D8]'
              )} rounded-lg p-5 border shadow-lg transition duration-300 ease-in-out`}
            >
              <div className="w-full h-40 rounded-md">
                <div 
                  className="w-full h-full overflow-y-auto rounded-md"
                  style={{ 
                    msOverflowStyle: 'auto', 
                    scrollbarWidth: 'thin',
                    cursor: 'pointer' 
                  }}
                >
                  <div className="relative w-full h-40 overflow-hidden rounded-md border border-gray-300 hover:shadow-md transition-shadow duration-200">
                    <div className={`${getThemeBasedClasses(
                      'bg-gray-800 border-gray-700', 
                      'bg-gray-200 border-gray-300'
                    )} h-5 w-full flex items-center px-2 border-b`}>
                      <div className="flex space-x-1.5">
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                        <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      </div>
                      <div className={`${getThemeBasedClasses(
                        'bg-gray-700', 
                        'bg-white'
                      )} h-3 rounded-sm flex-grow mx-2 text-[6px] overflow-hidden truncate px-1`}>
                        {project.liveLink}
                      </div>
                    </div>
                    
                    <div className="h-[calc(100%-20px)] w-full overflow-hidden">
                      {needsImagePreview(project.title) ? (
                        <div className="h-full overflow-hidden">
                          <div 
                            className="overflow-x-hidden overflow-y-auto rounded-b-md"
                            style={{ 
                              transform: "scale(0.3)",
                              transformOrigin: "0 0",
                              width: "calc(100% / 0.3)",
                              height: "calc(160px / 0.3)",
                              position: "relative"
                            }}>
                            <img 
                              src={project.imgSrc} 
                              alt={`${project.title} Preview`} 
                              style={{
                                width: "100%", 
                                minHeight: "100%",
                                objectFit: "cover",
                                display: "block"
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <iframe
                          src={project.liveLink}
                          title={project.title}
                          className="rounded-b-md"
                          style={getIframeStyles(project.title)}
                          loading="lazy"
                          sandbox="allow-same-origin allow-scripts allow-same-origin allow-forms"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <h3 className="text-lg font-bold mt-4">{project.title}</h3>
              <p className={`${getThemeBasedClasses(
                'text-gray-400', 
                'text-gray-600'
              )} text-sm transition-colors duration-300`}>
                {project.description}
              </p>

              <div className="flex flex-wrap gap-2 mt-3">
                {project.techStack.map((tech, i) => (
                  <span
                    key={i}
                    className={`${getThemeBasedClasses(
                      'bg-gray-800 text-gray-300',
                      'bg-gray-200 text-gray-700'
                    )} px-3 py-1 text-xs rounded-full transition-colors duration-300`}
                  >
                    {tech}
                  </span>
                ))}
              </div>
              
              {project.title !== "Flamehub Website & Mobile" &&
                project.title !== "George AI" && (
                <div className="flex gap-3 mt-4">
                  <a
                    href={project.liveLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 rounded-full bg-purple-600 text-white text-sm"
                  >
                    Live
                  </a>
                  {project.title !== "George AI" && project.repoCode && (
                    <a
                      href={project.repoCode}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${getThemeBasedClasses(
                        'bg-gray-800 text-gray-300',
                        'bg-gray-300 text-gray-700'
                      )} px-4 py-2 rounded-full text-sm transition-colors duration-300`}
                    >
                      Repository
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}        
        </div>
      </div>
    </div>
  );
}